import {type ReactNode, useCallback, useEffect, useId, useRef} from 'react';
import {useDismiss} from '../hooks/useDismiss';
import {PaperButton} from '../sticker/stickers';
import {InLayer} from './layer';
import {ErrorNotice} from './notice';
import {Typography} from './text';

/**
 * A confirmation for something that cannot be undone: a sentence, what it means, Cancel, and the
 * one destructive verb.
 *
 * This app's own: Malachite 2.1 has no dialog. A plain card centred over the dimmed app — portaled to
 * the overlay layer so the dim covers the whole window — `aria-modal`, focus moved onto Cancel when
 * it opens so a stray Enter cancels rather than confirms, and closed by Escape or a click on the dim
 * (useDismiss). Rendered only while `open`, so there is nothing to hide when it is not.
 */
export const ConfirmDialog = ({
    open,
    title,
    body,
    confirmLabel,
    busy = false,
    children,
    error,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    title: string;
    body: string;
    /** The verb: "Delete check-in". */
    confirmLabel: string;
    /** While the action is in flight the verb spins and neither button works. */
    busy?: boolean;
    /** Extra content under the body, such as a field to fill in. */
    children?: ReactNode;
    /** A failed confirm, shown in the dialog so it stays open. */
    error?: unknown;
    onConfirm: () => void;
    onCancel: () => void;
}) => {
    const card = useRef<HTMLDivElement>(null);
    const cancel = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const bodyId = useId();
    const dismiss = useCallback(() => {
        if (!busy) onCancel();
    }, [busy, onCancel]);

    useDismiss(card, open, dismiss);

    // Focus lands on Cancel as the dialog opens: the safe choice is the one a stray key reaches.
    useEffect(() => {
        if (open) cancel.current?.querySelector('button')?.focus();
    }, [open]);

    if (!open) return null;

    return (
        <InLayer>
            <div className="sd-dim">
                <div
                    ref={card}
                    className="sd-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    aria-describedby={bodyId}
                >
                    <h2 id={titleId} className="sd-dialog-title">
                        <Typography.Text size="large" fontWeight="semi-bold">
                            {title}
                        </Typography.Text>
                    </h2>
                    <p id={bodyId} className="sd-dialog-body">
                        <Typography.SecondaryText>{body}</Typography.SecondaryText>
                    </p>
                    {children}
                    {error ? <ErrorNotice title="Could not delete" error={error} /> : null}
                    <div className="sd-dialog-actions">
                        <div ref={cancel}>
                            <PaperButton variant="ghost" disabled={busy} onClick={onCancel}>
                                Cancel
                            </PaperButton>
                        </div>
                        <PaperButton disabled={busy} onClick={onConfirm}>
                            {busy ? 'Working...' : confirmLabel}
                        </PaperButton>
                    </div>
                </div>
            </div>
        </InLayer>
    );
};
